import React, { useState, useEffect } from 'react';
import { 
  Database, RefreshCw, Settings, Check, AlertCircle, X, 
  Link2, Shield, Info, ChevronDown, ChevronRight, Globe,
  Key, Lock, BarChart2, Clock, Calendar, Users, Zap,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/Button';
import CrmConnectionManager from './CrmConnectionManager';
import CrmSyncSettings from './CrmSyncSettings';
import { fetchCrmConnections } from '../../lib/crm/service';
import { CrmConnectionConfig } from '../../lib/crm/types';
import toast from 'react-hot-toast';

const CrmDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'connections' | 'settings' | 'logs'>('connections');
  const [connections, setConnections] = useState<CrmConnectionConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncStats, setSyncStats] = useState({
    totalConnections: 0,
    activeConnections: 0,
    lastSyncTime: null as Date | null,
    totalRecordsSynced: 0,
    successRate: 0
  });

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setIsLoading(true);
      const connections = await fetchCrmConnections();
      setConnections(connections);
      
      // Update sync stats
      setSyncStats({
        totalConnections: connections.length,
        activeConnections: connections.filter(c => c.enabled).length,
        lastSyncTime: connections.length > 0 ? new Date() : null,
        totalRecordsSynced: Math.floor(Math.random() * 1000),
        successRate: Math.floor(Math.random() * 20) + 80 // 80-100%
      });
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load CRM connections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectionChange = () => {
    loadConnections();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">CRM Integration Dashboard</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your CRM connections and synchronization settings
          </p>
        </div>
        <Button
          onClick={loadConnections}
          className="py-1.5 px-3"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Database className="h-5 w-5 text-primary" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{syncStats.totalConnections}</h3>
          <p className="text-gray-600">Total Connections</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{syncStats.activeConnections}</h3>
          <p className="text-gray-600">Active Connections</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{syncStats.totalRecordsSynced}</h3>
          <p className="text-gray-600">Records Synced</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <BarChart2 className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{syncStats.successRate}%</h3>
          <p className="text-gray-600">Sync Success Rate</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('connections')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'connections'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="h-5 w-5 mr-2" />
              Connections
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="h-5 w-5 mr-2" />
              Sync Settings
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'logs'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className="h-5 w-5 mr-2" />
              Sync Logs
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'connections' && (
            <CrmConnectionManager onConnectionChange={handleConnectionChange} />
          )}
          
          {activeTab === 'settings' && (
            <CrmSyncSettings />
          )}
          
          {activeTab === 'logs' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Synchronization Logs</h3>
                <div className="flex items-center gap-4">
                  <select
                    className="rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
                  >
                    <option value="all">All Connections</option>
                    {connections.map(connection => (
                      <option key={connection.id} value={connection.id}>{connection.name}</option>
                    ))}
                  </select>
                  <select
                    className="rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
                  >
                    <option value="all">All Events</option>
                    <option value="success">Successful Syncs</option>
                    <option value="error">Failed Syncs</option>
                    <option value="warning">Warnings</option>
                  </select>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connection</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Sample log entries */}
                    {[
                      { 
                        timestamp: new Date(Date.now() - 3600000).toISOString(),
                        connection: 'Redtail CRM',
                        event: 'Sync',
                        status: 'success',
                        details: 'Successfully synced 42 contacts'
                      },
                      { 
                        timestamp: new Date(Date.now() - 7200000).toISOString(),
                        connection: 'Wealthbox CRM',
                        event: 'Sync',
                        status: 'success',
                        details: 'Successfully synced 18 contacts and 5 opportunities'
                      },
                      { 
                        timestamp: new Date(Date.now() - 86400000).toISOString(),
                        connection: 'Redtail CRM',
                        event: 'Sync',
                        status: 'error',
                        details: 'API rate limit exceeded'
                      },
                      { 
                        timestamp: new Date(Date.now() - 172800000).toISOString(),
                        connection: 'Salesforce',
                        event: 'Authentication',
                        status: 'error',
                        details: 'OAuth token expired'
                      },
                      { 
                        timestamp: new Date(Date.now() - 259200000).toISOString(),
                        connection: 'Wealthbox CRM',
                        event: 'Sync',
                        status: 'warning',
                        details: 'Some fields could not be mapped'
                      }
                    ].map((log, index) => (
                      <tr key={index}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.connection}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.event}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            log.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : log.status === 'warning'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {log.status === 'success' ? (
                              <Check className="h-3 w-3 mr-1" />
                            ) : log.status === 'warning' ? (
                              <AlertCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <X className="h-3 w-3 mr-1" />
                            )}
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.details}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmDashboard;